<!-- Copyright: Seán I. O'Donoghue -->
<!-- The Settings header's email: the primary address is the edit button; editing opens a dark
     layer (as the name's) from the email downwards, with every email by kind (CLERK.md § Several
     emails per user; state in useEmailEdit). During an assistant's visit, the address only: no
     editor (useAssistantVisit; fullstack ROADMAP.md § Assistant account access) -->

<template>
  <div class="row email">
    <span v-if="isVisit" class="value">{{ modelValue }}</span>
    <button
      v-else
      ref="openButtonRef"
      class="value"
      type="button"
      title="Manage your emails"
      @click="openEditor">
      {{ modelValue }}
    </button>
    <AppModalProfileEditLayer :is-open="isEditingEmails" direction="down">
      <AppModalProfileEmailEditor ref="editorRef" />
      <template #actions>
        <AppModalProfileEditButtons
          :is-busy="isBusy"
          :can-cancel="pendingRemovalIds.length > 0"
          @cancel="discardEmails"
          @ok="confirmEmails(user)" />
      </template>
    </AppModalProfileEditLayer>
    <p class="visually-hidden" aria-live="polite">{{ announcement }}</p>
  </div>
</template>

<script setup>
import {nextTick, onMounted, ref, watch} from "vue";
import {useEventListener} from "@vueuse/core";
import {useRoute} from "vue-router";
import AppModalProfileEditLayer from "@/components/App/Modal/Profile/EditLayer";
import AppModalProfileEmailEditor from "@/components/App/Modal/Profile/EmailEditor";
import AppModalProfileEditButtons from "@/components/App/Modal/Profile/EditButtons";
import useUserSafe from "@/composables/useUserSafe";
import {useEmailEdit} from "@/composables/useEmailEdit";
import {loadAssistant} from "@/composables/useAssistant";
import {useAssistantVisit} from "@/composables/useAssistantVisit";
import {getIsProfileHash} from "@/composables/useProfileTabs";

defineProps(["modelValue"]);

const route = useRoute();
const {user} = useUserSafe();
const openButtonRef = ref(null);
const editorRef = ref(null);
const {
  isEditingEmails, isBusy, pendingRemovalIds, announcement, openEmails, discardEmails,
  confirmEmails,
} = useEmailEdit();

const {isVisit} = useAssistantVisit();

// the assistant known before the editor opens, so it opens at its full height
onMounted(loadAssistant);

async function openEditor() {
  openEmails();
  await nextTick();
  editorRef.value?.focus();
}

// only while the Profile shows the editor (not while #new-email adds an address)
const getIsActive = () => isEditingEmails.value && getIsProfileHash(route.hash);

// Escape: as Cancel; a click elsewhere in the dialog, or Enter away from a control: as OK
function onKeydown(event) {
  if (!getIsActive()) return;
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    return discardEmails();
  }
  if (event.key !== "Enter" || event.target?.closest?.("button, a, input, textarea")) return;
  event.preventDefault();
  confirmEmails(user.value);
}

// leaving the dialog (Close, Sign Out, ✕) discards the pending removals, as Cancel does
function onPointerDown(event) {
  if (!getIsActive() || !event.target?.closest?.("dialog")) return;
  if (event.target.closest(".open-field")) return;
  if (event.target.closest("[data-profile-exit], header .close")) return discardEmails();
  confirmEmails(user.value);
}

useEventListener(window, "keydown", onKeydown, {capture: true});
useEventListener(window, "pointerdown", onPointerDown, {capture: true});

// closed (OK, Cancel, Escape): the focus goes back to the email, if it was in the editor
watch(isEditingEmails, async (isOpen) => {
  if (isOpen) return;
  await nextTick();
  if (!document.activeElement || document.activeElement === document.body) {
    openButtonRef.value?.focus();
  }
});
</script>

<style scoped>
/* the affiliation's size and weight (ProfileMeta); the name's colour (Name.vue). Full width and
   positioned: the edit layer docks here, its top at the email */
.row {
  position: relative;
  align-self: stretch;
  display: flex;
  justify-content: center;
  max-width: 100%;
  font-size: 14px; /* as the affiliation, a clear step below the name (2026-10-20; was 16px) */
  font-weight: 600;
  line-height: 1.3;
}
/* the text itself is the button (global.css styles reset) */
.value {
  appearance: none;
  -webkit-appearance: none;
  background: none;
  border: none;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  min-width: 0;
  font: inherit;
  color: inherit;
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap; /* one line, cut with "…" (Seán, 2026-10-20; a long address wrapped) */
  text-overflow: ellipsis;
  cursor: pointer;
}
.value:hover {
  color: rgba(0, 0, 0, 0.65);
}
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
